import { MediaImageRepositoryProvider } from '@/scenes/engine/objects/media-repository/MediaImageRepositoryProvider'

interface ProcessImageOptions {
  maxEdgeSize?: number
  extractBoundingBox?: boolean
}

/**
 * Resize an image and optionally crop to its bounding box.
 * Returns the final image src ready for canvas use.
 */
export async function processImageForCanvas(
  dataUrl: string,
  options?: ProcessImageOptions,
  imageProcessing?: any
): Promise<string> {
  const { maxEdgeSize = 1280, extractBoundingBox = true } = options || {}
  const processing =
    imageProcessing ?? MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing

  const resized = await processing.resizeBlobToMaxEdgeSize(dataUrl, maxEdgeSize)

  if (extractBoundingBox) {
    const bb = await processing.extractBoundingBox(resized, false)
    const resizedHtmlImage = await processing.loadImage(resized)
    const cropped = await processing.cropHtmlImage(resizedHtmlImage, bb)
    return cropped.src
  }

  return resized
}

/**
 * Reads an uploaded file to a data URL and transcodes HEIC/HEIF to JPEG, so the
 * result is always safe to render in <img>/canvas. Use for user file uploads.
 */
export async function readImageFileAsCanvasReadyDataUrl(file: File): Promise<string> {
  const dataUrl = await readFileAsDataUrl(file)
  const processing =
    MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing
  return processing.convertToHeicToJpegIfNeeded(dataUrl, file)
}

/**
 * Promise wrapper around FileReader.readAsDataURL.
 */
export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (event) => {
      const result = event.target?.result as string
      if (result) {
        resolve(result)
      } else {
        reject(new Error('Failed to read file'))
      }
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}
